import React from 'react';
import { useController } from 'react-hook-form';

// components
import FormFieldLabel from '../FormFieldLabel';
import FormFieldError from '../FormFieldError';
import FormFieldDescription from '../FormFieldDescription';

// Keeps a grouped amount inside Number.MAX_SAFE_INTEGER, so the formatter never
// has to round what the user typed.
const MAX_GROUPED_DIGITS = 15;

const groupFormatter = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 });

const countDigits = (text) => (text.match(/\d/g) || []).length;

const formatGrouped = (value) => {
    const digits = String(value ?? '').replace(/\D/g, '');

    return digits ? groupFormatter.format(Number(digits)) : '';
};

const CONTROL_KEYS = [
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
];

const TextInput = ({ name, control, rules, label, type = 'text', prefix, description, config, grouping = false, ...props }) => {
    const {
        field,
        fieldState: { error }
    } = useController({
        name,
        control,
        rules
    });

    // A grouped field shows "930.000" but keeps "930000" as the form value, so
    // validation and the payload never see the separators.
    const isGrouped = 'numeric' === type && grouping;
    const inputRef = React.useRef(null);
    const caretDigitsRef = React.useRef(null);

    // Re-grouping shifts every separator, so a caret restored by character index
    // would drift. Count in digits instead and map back after the render.
    React.useLayoutEffect(() => {
        if (null === caretDigitsRef.current || !inputRef.current) return;

        const targetDigits = caretDigitsRef.current;
        caretDigitsRef.current = null;

        const text = inputRef.current.value;
        let seen = 0;
        let position = 0;

        for (let i = 0; i < text.length; i++) {
            position = i + 1;

            if (/\d/.test(text[i])) {
                seen++;
                if (seen === targetDigits) break;
            }
        }

        if (0 === targetDigits) position = 0;

        inputRef.current.setSelectionRange(position, position);
    });

    const handleChange = (e) => {
        if (isGrouped) {
            const caret = e.target.selectionStart ?? e.target.value.length;
            caretDigitsRef.current = countDigits(e.target.value.slice(0, caret));
            field.onChange(e.target.value.replace(/\D/g, '').slice(0, MAX_GROUPED_DIGITS));
        } else if (type === 'numeric') {
            // For numeric inputs, filter to allow only valid numeric characters
            let value = e.target.value.replace(/[^0-9.-]/g, '');
            // Ensure only one decimal point
            const parts = value.split('.');
            if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
            }
            // Ensure minus sign only at the beginning
            value = value.replace(/-/g, (match, offset) => offset === 0 ? match : '');
            field.onChange(value);
        } else {
            field.onChange(e.target.value);
        }
    };

    const handleGroupedKeyDown = (e) => {
        if ('Backspace' === e.key) {
            const { value, selectionStart, selectionEnd } = e.target;

            // Backspacing onto a thousands separator has to remove the digit in
            // front of it, otherwise the key appears to do nothing.
            if (selectionStart === selectionEnd && selectionStart > 0 && !/\d/.test(value[selectionStart - 1])) {
                e.preventDefault();

                const digitsBefore = countDigits(value.slice(0, selectionStart));
                if (0 === digitsBefore) return;

                const digits = value.replace(/\D/g, '');
                caretDigitsRef.current = digitsBefore - 1;
                field.onChange(digits.slice(0, digitsBefore - 1) + digits.slice(digitsBefore));
            }

            return;
        }

        if (!CONTROL_KEYS.includes(e.key) && !e.ctrlKey && !e.metaKey && !/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleKeyDown = (e) => {
        if (isGrouped) {
            handleGroupedKeyDown(e);
            return;
        }

        if (type === 'numeric') {
            // Handle numeric input
            const allowedKeys = CONTROL_KEYS;

            if (!allowedKeys.includes(e.key) &&
                !e.ctrlKey && !e.metaKey &&
                !/^[0-9.-]$/.test(e.key)) {
                e.preventDefault();
            }

            // Prevent multiple decimal points
            if (e.key === '.' && e.target.value.includes('.')) {
                e.preventDefault();
            }

            // Prevent multiple minus signs or minus not at the beginning
            if (e.key === '-') {
                if (e.target.value.includes('-') || e.target.selectionStart > 0) {
                    e.preventDefault();
                }
            }
        }
    };

    const inputType = type === 'numeric' ? 'text' : type;

    return (
        <div
            className={`FormField FormField--text FormField--${type}${prefix ? ' FormField--hasPrefix' : ''}`}
        >
            <FormFieldLabel label={label} name={name} rules={rules}/>
            <div className="FormField__inputWrapper">
                {prefix && <span className="FormField__prefix">{prefix}</span>}
                <input
                    {...props}
                    ref={inputRef}
                    id={name}
                    type={inputType}
                    value={isGrouped ? formatGrouped(field.value) : (field.value || '')}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    inputMode={type === 'numeric' ? 'decimal' : undefined}
                    pattern={type === 'numeric' ? '[0-9.\\-]*' : undefined}
                />
            </div>
            {error ? (
                <FormFieldError error={error} />
            ) : (
                <FormFieldDescription description={description} />
            )}
        </div>
    );
}

export default TextInput;
